"use client";

import React from "react";
import { Loader, Shield, Lock, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function SecureYourAssets() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="37324" id="get-your-free-estimate" ref={ref} className="dark relative w-full py-24 overflow-hidden opacity-100 translate-y-0 transition-all duration-1000 ease-out !bg-transparent !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/01-18956-columbus-mill-dr-new-caney-tx-77357-usa-006-scaled-1.png)] !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/garage_door_repair_hero_bg.png)] !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/garage_door_repair_hero_bg.png)] !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/AEB115BA-8E4E-4576-A946-6ADB8A0392DD.png)] !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/AEB115BA-8E4E-4576-A946-6ADB8A0392DD.png)] text-foreground/31">
      {}
      <div className="absolute inset-0 bg-zinc-950/90 backdrop-blur-[2px] !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/ChatGPT-Image-Mar-28-2026-at-03_03_28-PM.png)] !opacity-[0.12]" />

      <div className="relative container mx-auto px-4 z-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 space-y-4">
            <div className="inline-flex items-center justify-center p-3 rounded-full bg-green-600/10 border border-green-600/20 mb-4">
              <Shield className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-widest uppercase text-green-500 drop-shadow-md">
              Get Your Free Estimate
            </h2>
            <p className="text-muted-foreground text-lg max-w-lg mx-auto">Call or Text <span className="text-green-500 font-bold" data-ast-id="f:/src/pages/Home/sections/SecureYourAssets.tsx|sig:9fdwtr" data-ast-start="1943" data-ast-end="2005">713-304-4798</span> for immediate assistance.</p>
          </div>

          <div className="border border-white/10 backdrop-blur-md p-8 rounded-xl shadow-2xl ring-1 ring-white/5 !opacity-[0.81] !bg-[rgba(0,0,0,1)] md:p-10">
            <FormProvider formKey="free_estimate_form_home" formId="free_estimate_form_home" sectionName="secure-your-assets" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            asset_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            company: {
              required: false
            },
            asset_type: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="GET FREE ESTIMATE" successMessage="Estimate request received. Steve will contact you shortly to discuss your project.">
              <SecureAssetsForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function SecureAssetsForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center border border-green-600/50">
          <CircleCheck className="w-10 h-10 text-green-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-white">
            Request Submitted
          </h3>
          <p className="text-green-400 font-medium">{successMessage}</p>
        </div>
        <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-sm text-muted-foreground max-w-sm">
          Thank you for choosing SchaeferGarage. We prioritize veteran-owned reliability and same-day response.
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-green-500/90 font-semibold uppercase tracking-wider text-xs">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-black/50 border-white/10 focus-visible:ring-green-600/50 focus-visible:border-green-600 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-green-500/90 font-semibold uppercase tracking-wider text-xs flex items-center gap-2">
                Phone Number <Lock className="w-3 h-3" />
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="713-304-4798" className="bg-black/50 border-white/10 focus-visible:ring-green-600/50 focus-visible:border-green-600 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-semibold uppercase tracking-wider text-xs">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-black/50 border-white/10 focus-visible:ring-green-600/50 focus-visible:border-green-600 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-semibold uppercase tracking-wider text-xs">
                Service Location
              </FormLabel>
              <FormControl>
                <Input placeholder="Splendora, TX" className="bg-black/50 border-white/10 focus-visible:ring-green-600/50 focus-visible:border-green-600 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="asset_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-green-500/90 font-semibold uppercase tracking-wider text-xs">
              Service Category
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-black/50 border-white/10 focus:ring-green-600/50 focus:border-green-600 h-12 text-foreground">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-zinc-900 border-white/10 text-foreground">
                {["Garage Door Repair", "New Door Installation", "Commercial Services", "Residential Maintenance", "Emergency 24/7 Service"].map((option, i) => <SelectItem key={option} value={option} data-index={i} className="focus:bg-green-600/20 focus:text-green-500">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-semibold uppercase tracking-wider text-xs">
              Project Details
            </FormLabel>
            <FormControl>
              {}
              <textarea className="flex min-h-[100px] w-full rounded-md border border-white/10 bg-black/50 px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-green-600/50 focus-visible:border-green-600 disabled:cursor-not-allowed disabled:opacity-50 resize-none" placeholder="Describe your garage door issues, custom project needs, or upload photo details..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-red-400 text-sm font-medium text-center">
            {submitError}
          </p>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white rounded-md px-4 py-2 font-medium">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Sending Request...
          </> : "Get Free Estimate"}
      </Button>
    </Form>;
}